import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { MinusIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/solid';
import { createProductUrl } from '../../utils/seo';
import useInstantCode from '../../hooks/useInstantCode';
import { getProductDisplayId } from '../../utils/skuSecrecy';

import {
  selectCartIsOpen,
  selectCartItems,
  selectCartTotal,
  selectCartItemCount,
  selectCartLoading,
  closeCart,
  updateCartItem,
  removeFromCart,
  updateItemLocal,
  removeItemLocal,
} from '../../store/slices/cartSlice';
import { selectIsAuthenticated, selectUser } from '../../store/slices/authSlice';

const CartSidebar = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectCartIsOpen);
  const cartItems = useSelector(selectCartItems);
  const cartSkus = cartItems.map(i => i.product?.sku).filter(Boolean);
  const { getInstantCode, canSeeInstant } = useInstantCode(cartSkus);
  const cartTotal = useSelector(selectCartTotal);
  const itemCount = useSelector(selectCartItemCount);
  const loading = useSelector(selectCartLoading);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectUser);

  const handleClose = () => {
    dispatch(closeCart());
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    
    if (isAuthenticated) {
      dispatch(updateCartItem({ product_id: productId, quantity: newQuantity }));
    } else {
      dispatch(updateItemLocal({ product_id: productId, quantity: newQuantity }));
    }
  };

  const handleRemoveItem = (productId) => {
    if (isAuthenticated) {
      dispatch(removeFromCart(productId));
    } else {
      dispatch(removeItemLocal(productId));
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(price);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-80 sm:w-96">
                  <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                    {/* Header */}
                    <div className="flex items-start justify-between p-3 border-b border-gray-200">
                      <Dialog.Title className="text-lg font-medium text-gray-900">
                        Shopping Cart ({itemCount})
                      </Dialog.Title>
                      <div className="ml-3 flex h-7 items-center">
                        <button
                          type="button"
                          className="relative -m-2 p-2 text-gray-400 hover:text-gray-500"
                          onClick={handleClose}
                        >
                          <span className="absolute -inset-0.5" />
                          <span className="sr-only">Close panel</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                    </div>

                    {/* Cart Content */}
                    <div className="flex-1 overflow-y-auto p-3">
                      {cartItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64">
                          <p className="mt-4 text-sm text-gray-500">Your cart is empty</p>
                          <button
                            onClick={handleClose}
                            className="mt-4 btn-primary"
                          >
                            Continue Shopping
                          </button>
                        </div>
                      ) : (
                        <div className="flow-root">
                          <ul className="-my-6 divide-y divide-gray-200">
                            {cartItems.map((item) => (
                              <li key={item.id} className="flex py-6">
                                <div className="flex flex-1 flex-col">
                                  <div>
                                    <div className="flex justify-between text-base font-medium text-gray-900">
                                      <h3>
                                        <Link
                                          to={createProductUrl(item.product)}
                                          onClick={handleClose}
                                          className="hover:text-primary-600"
                                        >
                                          {item.product.name}
                                        </Link>
                                      </h3>
                                      <p className="ml-4">{formatPrice(item.line_total)}</p>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-500">
                                      {getProductDisplayId(user, item.product) === item.product.sku ? 'SKU: ' : 'ID: '}{getProductDisplayId(user, item.product)}
                                      {canSeeInstant && getInstantCode(item.product.sku) && (
                                        <span className="ml-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 px-1 py-0.5 rounded">
                                          Instant: {getInstantCode(item.product.sku)}
                                        </span>
                                      )}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {item.product.make?.name} {item.product.model?.name}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      {item.product.position?.name}
                                    </p>
                                    {item.product.year_from && item.product.year_to && (
                                      <p className="text-sm text-gray-500">
                                        {item.product.year_from} - {item.product.year_to}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center justify-between mt-4">
                                    <div className="flex items-center space-x-2">
                                      <button
                                        onClick={() => handleUpdateQuantity(item.product.id, item.quantity - 1)}
                                        disabled={item.quantity <= 1 || loading}
                                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <MinusIcon className="h-4 w-4" />
                                      </button>
                                      <span className="font-medium w-8 text-center">
                                        {item.quantity}
                                      </span>
                                      <button
                                        onClick={() => handleUpdateQuantity(item.product.id, item.quantity + 1)}
                                        disabled={loading}
                                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <PlusIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                    <div className="flex">
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveItem(item.product.id)}
                                        disabled={loading}
                                        className="font-medium text-red-600 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    {cartItems.length > 0 && (
                      <div className="border-t border-gray-200 p-3 space-y-3">
                        <div className="flex justify-between text-base font-medium text-gray-900">
                          <p>Subtotal</p>
                          <p>{formatPrice(cartTotal)}</p>
                        </div>
                        <p className="text-sm text-gray-500">
                          Shipping and taxes calculated at checkout.
                        </p>
                        <div className="space-y-2">
                          <Link
                            to="/cart"
                            onClick={handleClose}
                            className="w-full btn-outline text-center"
                          >
                            View Cart
                          </Link>
                          <Link
                            to="/checkout"
                            onClick={handleClose}
                            className="w-full btn-primary text-center"
                          >
                            Checkout
                          </Link>
                        </div>
                        <div className="text-center">
                          <button
                            onClick={handleClose}
                            className="text-sm font-medium text-primary-600 hover:text-primary-500"
                          >
                            Continue Shopping
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default CartSidebar;
